import { useAutoSave } from '@/hooks/useAutoSave';
import { useCompileDiagnostics } from '@/hooks/useCompileDiagnostics';
import * as fileService from '@/services/file.service';
import { useDeployStore, useEditorStore } from '@/store';
import { Rocket, Sparkles } from 'lucide-react';
import { useCallback } from 'react';
import CodeEditor from './CodeEditor';
import EditorToolbar from './EditorToolbar';
import TabBar from './TabBar';

interface EditorPanelProps {
  workspaceId: string | null;
  onQuickStart?: () => void;
}

export default function EditorPanel({ workspaceId, onQuickStart }: EditorPanelProps) {
  const { tabs, activeTabId, openFiles, updateFileContent, saveFile } = useEditorStore();
  const { compileResult } = useDeployStore();

  // Auto-save wiring — reads ide-autoSave and ide-autoSaveDelay from localStorage
  useAutoSave(workspaceId);

  const activeTab = tabs.find((t) => t.id === activeTabId) ?? null;
  const content = activeTab ? (openFiles.get(activeTab.path) ?? '') : '';

  // Push compile errors as inline Monaco markers (squiggly underlines)
  // Also runs a background security scan after successful compile and adds findings as markers
  useCompileDiagnostics(compileResult, activeTab?.path ?? null, content || undefined);

  const handleChange = useCallback(
    (value: string) => {
      if (activeTab) {
        updateFileContent(activeTab.path, value);
      }
    },
    [activeTab, updateFileContent],
  );

  const handleSave = useCallback(async () => {
    if (!activeTab || !workspaceId) return;
    const fileContent = openFiles.get(activeTab.path);
    if (fileContent === undefined) return;

    try {
      await fileService.saveFile(workspaceId, activeTab.path, fileContent);
      saveFile(activeTab.path);
    } catch (err) {
      console.error('Failed to save file:', err);
    }
  }, [activeTab, workspaceId, openFiles, saveFile]);

  if (!activeTab) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-[#1e1e1e] text-gray-500">
        <div className="flex items-center justify-center w-20 h-20 mb-6 rounded-2xl bg-gradient-to-br from-purple-500/10 to-blue-500/10">
          <Rocket className="w-10 h-10 text-purple-400/40" />
        </div>
        <p className="text-sm text-[#aaa] font-medium">Ready to build</p>
        <p className="text-xs mt-1.5 text-gray-600 max-w-[260px] text-center">
          Open a file from the explorer, or let AI build and deploy a contract for you.
        </p>
        {onQuickStart && (
          <button
            onClick={onQuickStart}
            className="mt-5 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white text-xs font-semibold shadow-lg shadow-purple-500/20 transition-all"
          >
            <Sparkles className="w-4 h-4" />
            Quick Start — AI Build & Deploy
          </button>
        )}
        <p className="mt-3 text-[10px] text-gray-700">Ctrl+Shift+N</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <TabBar />
      <EditorToolbar />
      <div className="flex-1 min-h-0">
        <CodeEditor
          key={activeTab.id}
          value={content}
          language={activeTab.language}
          path={activeTab.path}
          onChange={handleChange}
          onSave={handleSave}
        />
      </div>
    </div>
  );
}
